import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    RefreshCw,
    Table,
    DollarSign,
    Users,
    Building2,
    X,
    UserPlus,
    BarChart3,
    Settings,
    FileText,
    Eye
} from 'lucide-react';
import { getUser, logout, apiGet, apiPost, getJWT } from '../auth';
import { useLanguage } from '../contexts/LanguageContext';
import CompanyManager from '../components/admin/CompanyManager';
import { AuditLogsPage } from './admin/AuditLogsPage';
import { TrashPage } from './admin/TrashPage';

interface UserData {
    id: string;
    email: string;
    created_at: string;
    last_sign_in_at: string;
    calculation_credits: number;
    pdf_credits: number;
    subscription_tier: string;
    monthly_usage_calc: number;
    monthly_usage_pdf: number;
    role?: string;
}

const SUPER_ADMIN_EMAIL = "superadm@lashingplan.com";

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);
    const { t } = useLanguage();
    const [error, setError] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
    const [addCreditsForm, setAddCreditsForm] = useState({ calc: 0, pdf: 0 });
    const [actionLoading, setActionLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'companies' | 'logs' | 'settings' | 'trash'>('overview');
    const [showCreateUserForm, setShowCreateUserForm] = useState(false);
    const [createUserForm, setCreateUserForm] = useState({ email: '', password: '', calc: 0, pdf: 0, company_id: '', role: 'basic' });
    const [editRole, setEditRole] = useState('basic');
    const [selectedRoleUser, setSelectedRoleUser] = useState<UserData | null>(null);

    // Enterprise State Additions
    const [stats, setStats] = useState<any>(null);
    const [settingsList, setSettingsList] = useState<any[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [bulkCalcCredits, setBulkCalcCredits] = useState(0);
    const [bulkPdfCredits, setBulkPdfCredits] = useState(0);
    const [bulkRole, setBulkRole] = useState('');

    useEffect(() => {
        const user = getUser();
        if (!user || user.email !== SUPER_ADMIN_EMAIL) {
            navigate('/');
            return;
        }
        setCurrentUser(user);
        fetchDataForTab(activeTab);
    }, [navigate, activeTab]);

    const fetchDataForTab = (tab: string) => {
        if (tab === 'users') fetchUsers();
        else if (tab === 'overview') fetchStats();
        else if (tab === 'settings') fetchSettings();
    };

    const fetchStats = async () => {
        setLoading(true);
        try { const data = await apiGet<any>('/api/admin/dashboard/stats'); setStats(data); }
        catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const fetchSettings = async () => {
        setLoading(true);
        try { const data = await apiGet<any[]>('/api/admin/settings'); setSettingsList(data); }
        catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    const fetchUsers = async () => {
        if (!getJWT()) return; // Exit early during logout teardown 
        setLoading(true);
        setError(null);
        try {
            const data = await apiGet<UserData[]>('/api/admin/users');
            setUsers(data);
        } catch (err: any) {
            if (err.message && (err.message.includes('401') || err.message.includes('403'))) {
                console.warn(`Auth error: Redirecting to login`);
                logout();
                navigate('/login');
                return;
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCredits = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return;

        setActionLoading(true);
        try {
            await apiPost('/api/admin/credits/add', {
                user_id: selectedUser.id,
                calculation_credits: Number(addCreditsForm.calc),
                pdf_credits: Number(addCreditsForm.pdf)
            });

            // Refresh list
            await fetchUsers();
            setSelectedUser(null);
            setAddCreditsForm({ calc: 0, pdf: 0 });
            alert("Credits updated successfully!");
        } catch (err: any) {
            alert(`Error updating credits: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditRole = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedRoleUser) return;

        setActionLoading(true);
        try {
            if (editRole !== (selectedRoleUser.role || 'basic')) {
                await apiPost(`/api/admin/users/${selectedRoleUser.id}/profile`, {
                    role: editRole
                });
            }

            // Refresh list
            await fetchUsers();
            setSelectedRoleUser(null);
            alert("Role updated successfully!");
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!createUserForm.email || !createUserForm.password) {
            alert("Email and Password are required.");
            return;
        }

        setActionLoading(true);
        try {
            await apiPost('/api/admin/users/create', {
                email: createUserForm.email,
                password: createUserForm.password,
                calculation_credits: Number(createUserForm.calc),
                pdf_credits: Number(createUserForm.pdf),
                company_id: createUserForm.company_id || null,
                role: createUserForm.role
            });

            await fetchUsers();
            setShowCreateUserForm(false);
            setCreateUserForm({ email: '', password: '', calc: 0, pdf: 0, company_id: '', role: 'basic' });
            alert("User created successfully!");
        } catch (err: any) {
            alert(`Error creating user: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleBulkUpdate = async () => {
        if (selectedUserIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to update ${selectedUserIds.length} users?`)) return;
        
        setActionLoading(true);
        try {
            await apiPost('/api/admin/users/bulk-update', {
                user_ids: selectedUserIds,
                calc_credits: bulkCalcCredits,
                pdf_credits: bulkPdfCredits,
                role: bulkRole || null
            });
            alert("Bulk update successful!");
            setSelectedUserIds([]);
            setBulkCalcCredits(0);
            setBulkPdfCredits(0);
            setBulkRole('');
            fetchUsers();
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleImpersonate = async (email: string) => {
        if (!window.confirm(`Are you sure you want to generate a ghost login link for ${email}?`)) return;
        try {
            const res = await apiPost<{link: string}>('/api/admin/users/impersonate', { email });
            prompt("Ghost login link generated! Copy and open in an incognito window:", res.link);
        } catch (err: any) {
            alert(`Error generating link: ${err.message}`);
        }
    };

    const toggleUserSelection = (id: string) => {
        if (selectedUserIds.includes(id)) {
            setSelectedUserIds(selectedUserIds.filter(uid => uid !== id));
        } else {
            setSelectedUserIds([...selectedUserIds, id]);
        }
    };

    const toggleAllUsers = () => {
        if (selectedUserIds.length === users.length) setSelectedUserIds([]);
        else setSelectedUserIds(users.map(u => u.id));
    };

    if (!currentUser) {
        return null;
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-[#070910] pt-24 pb-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto space-y-6">

                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <Table className="h-8 w-8 text-indigo-600" />
                            Admin Dashboard
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400">Manage users and credits</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => fetchDataForTab(activeTab)}
                            disabled={loading}
                            className="bg-white p-2 rounded-lg shadow-sm border border-slate-200 text-slate-500 hover:text-orange-600 hover:border-orange-200 transition-all"
                            title="Refresh Data"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={handleLogout}
                            className="bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-200 text-slate-600 font-medium hover:text-red-600 hover:border-red-200 transition-all flex items-center gap-2"
                        >
                            Logout
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-200">
                        {error}
                    </div>
                )}
                
                {/* TABS Navigation */}
                <div className="flex border-b border-slate-200 dark:border-slate-700 overflow-x-auto overflow-y-hidden">
                    <button onClick={() => setActiveTab('overview')} className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'overview' ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}>
                        <BarChart3 className="w-4 h-4" /> Overview
                    </button>
                    <button onClick={() => setActiveTab('users')} className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'users' ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}>
                        <Users className="w-4 h-4" /> Users
                    </button>
                    <button onClick={() => setActiveTab('companies')} className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'companies' ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}>
                        <Building2 className="w-4 h-4" /> Companies
                    </button>
                    <button onClick={() => setActiveTab('logs')} className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'logs' ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}>
                        <FileText className="w-4 h-4" /> Audit Logs
                    </button>
                    <button onClick={() => setActiveTab('trash')} className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'trash' ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}>
                        <RefreshCw className="w-4 h-4" /> Trash
                    </button>
                    <button onClick={() => setActiveTab('settings')} className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${activeTab === 'settings' ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'}`}>
                        <Settings className="w-4 h-4" /> Settings
                    </button>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-6">
                        <div className="bg-white dark:bg-[#0D111C] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400"><Users className="w-6 h-6"/></div>
                                <div><p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Users</p><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_users}</p></div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-[#0D111C] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400"><UserPlus className="w-6 h-6"/></div>
                                <div><p className="text-sm font-medium text-slate-500 dark:text-slate-400">New This Week</p><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.new_users_week}</p></div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-[#0D111C] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400"><BarChart3 className="w-6 h-6"/></div>
                                <div><p className="text-sm font-medium text-slate-500 dark:text-slate-400">Calcs (All Time)</p><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_calculations_month}</p></div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-[#0D111C] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl text-purple-600 dark:text-purple-400"><FileText className="w-6 h-6"/></div>
                                <div><p className="text-sm font-medium text-slate-500 dark:text-slate-400">PDFs (All Time)</p><p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_pdfs_month}</p></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Audit Logs Tab */}
                {activeTab === 'logs' && (
                    <div className="mt-6">
                        <AuditLogsPage />
                    </div>
                )}
                
                {/* Trash Tab */}
                {activeTab === 'trash' && (
                    <div className="mt-6">
                        <TrashPage />
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="bg-white dark:bg-[#0D111C] shadow-xl rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 p-6 max-w-2xl mt-6">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Global System Settings</h2>
                        <div className="space-y-6">
                            {settingsList.map((setting) => (
                                <div key={setting.key} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                                    <h3 className="font-semibold text-slate-900 dark:text-white">{setting.key}</h3>
                                    <p className="text-sm text-slate-500 mb-2">{setting.description}</p>
                                    <pre className="bg-slate-100 dark:bg-slate-900 p-3 rounded text-xs overflow-x-auto">{JSON.stringify(setting.value, null, 2)}</pre>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Companies Tab */}
                {activeTab === 'companies' && (
                    <CompanyManager />
                )}

                {/* Users Table */}
                {activeTab === 'users' && (
                <div className="bg-white dark:bg-slate-800 shadow-xl rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 mt-6">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center w-full sm:w-auto">
                            {selectedUserIds.length > 0 && (
                                <div className="flex flex-wrap items-center gap-3 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-lg border border-indigo-100 dark:border-indigo-800 w-full">
                                    <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300 pr-2 border-r border-indigo-200 dark:border-indigo-700 whitespace-nowrap">
                                        {selectedUserIds.length} users
                                    </span>
                                    <input type="number" placeholder="+Calc" className="w-16 text-xs px-2 py-1 rounded border dark:bg-[#161B2E] dark:border-white/10 focus:ring-1 focus:ring-indigo-500 outline-none w-full sm:w-16" value={bulkCalcCredits} onChange={e => setBulkCalcCredits(Number(e.target.value))} />
                                    <input type="number" placeholder="+PDF" className="w-16 text-xs px-2 py-1 rounded border dark:bg-slate-700 dark:border-slate-600 focus:ring-1 focus:ring-indigo-500 outline-none w-full sm:w-16" value={bulkPdfCredits} onChange={e => setBulkPdfCredits(Number(e.target.value))} />
                                    <select className="text-xs px-2 py-1 rounded border dark:bg-slate-700 dark:border-slate-600 focus:ring-1 focus:ring-indigo-500 outline-none w-full sm:w-auto" value={bulkRole} onChange={e => setBulkRole(e.target.value)}>
                                        <option value="">No Role Change</option>
                                        <option value="basic">Basic User</option>
                                        <option value="advance">Advance User</option>
                                        <option value="surveyor">Surveyor</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    <button onClick={handleBulkUpdate} className="text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1 rounded text-xs font-medium transition-colors shadow-sm ml-auto sm:ml-0">
                                        Apply
                                    </button>
                                </div>
                            )}
                        </div>
                        <button 
                            onClick={() => setShowCreateUserForm(true)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm shrink-0 whitespace-nowrap ml-auto"
                        >
                            <UserPlus className="w-4 h-4"/>
                            Create User
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                            <thead className="bg-slate-50 dark:bg-slate-900/50">
                                <tr>
                                    <th className="px-6 py-3 w-10"><input type="checkbox" onChange={toggleAllUsers} checked={users.length > 0 && selectedUserIds.length === users.length} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" /></th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Credits (Calc/PDF)</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Sub. Tier</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Usage (Mo)</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Last Active</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-[#0D111C] divide-y divide-slate-200 dark:divide-white/5">
                                {users.map((u) => (
                                    <tr key={u.id} className={`hover:bg-slate-50 dark:hover:bg-white/5 transition-colors ${selectedUserIds.includes(u.id) ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUserSelection(u.id)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold shrink-0">
                                                    {u.email.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-slate-900 dark:text-white">{u.email}</div>
                                                    <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{u.id.substring(0, 8)}...</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize 
                                                ${
                                                    u.role === 'superuser' ? 'bg-red-100 text-red-800 border border-red-200' :
                                                    u.role === 'admin' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                                                    u.role === 'surveyor' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                                    u.role === 'advance' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                                                    'bg-slate-100 text-slate-800'
                                                }`}>
                                                {u.role || 'basic'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    Calc: {u.calculation_credits}
                                                </span>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                    PDF: {u.pdf_credits}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize 
                                                ${u.subscription_tier === 'premium' ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-slate-100 text-slate-800'}`}>
                                                {u.subscription_tier}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                            <div className="flex flex-col">
                                                <span>{u.monthly_usage_calc} calcs</span>
                                                <span className="text-xs text-slate-400">{u.monthly_usage_pdf} PDFs</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                            {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleImpersonate(u.email)}
                                                className="text-amber-500 hover:text-amber-700 dark:text-amber-400 mr-4"
                                                title="Ghost Login"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedRoleUser(u);
                                                    setEditRole(u.role || 'basic');
                                                }}
                                                className="text-emerald-600 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-300 mr-4"
                                            >
                                                Edit Role
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedUser(u);
                                                    setAddCreditsForm({ calc: 0, pdf: 0 });
                                                }}
                                                className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"
                                            >
                                                Add Credits
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}

                {/* Add Credits Modal */}
                {selectedUser && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setSelectedUser(null)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <DollarSign className="h-6 w-6 text-green-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-slate-900 dark:text-white" id="modal-title">
                                                Add Credits for {selectedUser.email}
                                            </h3>
                                            <div className="mt-2 text-left">
                                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                                    Enter the amount of credits to add (or subtract using negative numbers).
                                                </p>
                                                <form onSubmit={handleAddCredits} className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Add Calc Credits</label>
                                                            <input
                                                                type="number"
                                                                value={addCreditsForm.calc}
                                                                onChange={(e) => setAddCreditsForm({ ...addCreditsForm, calc: parseInt(e.target.value) })}
                                                                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Add PDF Credits</label>
                                                            <input
                                                                type="number"
                                                                value={addCreditsForm.pdf}
                                                                onChange={(e) => setAddCreditsForm({ ...addCreditsForm, pdf: parseInt(e.target.value) })}
                                                                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                                        <button
                                                            type="submit"
                                                            disabled={actionLoading}
                                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                                        >
                                                            {actionLoading ? t('common.saving') : t('common.save_changes')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedUser(null)}
                                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600 dark:hover:bg-slate-600"
                                                        >
                                                            {t('common.cancel')}
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Role Modal */}
                {selectedRoleUser && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setSelectedRoleUser(null)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <UserPlus className="h-6 w-6 text-emerald-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-slate-900 dark:text-white" id="modal-title">
                                                Edit Role: {selectedRoleUser.email}
                                            </h3>
                                            <div className="mt-2 text-left">
                                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                                    Select a primary role for this user to control permissions.
                                                </p>
                                                <form onSubmit={handleEditRole} className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">User Role</label>
                                                        <select
                                                            value={editRole}
                                                            onChange={(e) => setEditRole(e.target.value)}
                                                            className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                                        >
                                                            <option value="basic">Basic User</option>
                                                            <option value="advance">Advance User</option>
                                                            <option value="surveyor">Surveyor</option>
                                                            <option value="admin">Admin</option>
                                                            <option value="superuser">Superuser</option>
                                                        </select>
                                                    </div>
                                                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                                        <button
                                                            type="submit"
                                                            disabled={actionLoading}
                                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                                        >
                                                            {actionLoading ? t('common.saving') : t('common.save_changes')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedRoleUser(null)}
                                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600 dark:hover:bg-slate-600"
                                                        >
                                                            {t('common.cancel')}
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create User Modal */}
                {showCreateUserForm && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowCreateUserForm(false)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
                                <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="flex justify-between items-center mb-5">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                                <UserPlus className="h-5 w-5" />
                                            </div>
                                            <h3 className="text-lg leading-6 font-medium text-slate-900 dark:text-white" id="modal-title">
                                                Create New User
                                            </h3>
                                        </div>
                                        <button onClick={() => setShowCreateUserForm(false)} className="text-slate-400 hover:text-slate-600">
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <form onSubmit={handleCreateUser} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Email Address *</label>
                                            <input
                                                type="email"
                                                required
                                                value={createUserForm.email}
                                                onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                                                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Temporary Password *</label>
                                            <input
                                                type="password"
                                                required
                                                minLength={6}
                                                value={createUserForm.password}
                                                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                                                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">User Role</label>
                                            <select
                                                required
                                                value={createUserForm.role}
                                                onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                                                className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                            >
                                                <option value="basic">Basic User</option>
                                                <option value="advance">Advance User</option>
                                                <option value="surveyor">Surveyor</option>
                                                <option value="admin">Admin</option>
                                                <option value="superuser">Superuser</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Initial Calc Credits</label>
                                                <input
                                                    type="number"
                                                    value={createUserForm.calc}
                                                    onChange={(e) => setCreateUserForm({ ...createUserForm, calc: parseInt(e.target.value) })}
                                                    className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Initial PDF Credits</label>
                                                <input
                                                    type="number"
                                                    value={createUserForm.pdf}
                                                    onChange={(e) => setCreateUserForm({ ...createUserForm, pdf: parseInt(e.target.value) })}
                                                    className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white px-3 py-2 border"
                                                />
                                            </div>
                                        </div>
                                        <div className="pt-4 mt-2 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowCreateUserForm(false)}
                                                className="inline-flex justify-center rounded-md border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600 dark:hover:bg-slate-600"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={actionLoading}
                                                className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"
                                            >
                                                {actionLoading ? 'Creating...' : 'Create User'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
